import React, { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useCollection, getCardData } from "../collectionProvider/CollectionProvider";
import { useUser } from "../../users/providers/UserProvider";
import Card from "../components/card/Card";
import CardDetailModal from "../components/card/CardDetailModal";
import Loader from "../../styles/Loader";
import ListCardModal from "../../collection/ListCardModal";
import { readDraft } from "../../native/drafts";
import CardChallengeModal from "../../verification/CardChallengeModal";
import { getMyCardChallenges } from "../../verification/verificationApi";
import { isExpiredChallenge } from "../../verification/challengeExpiry";
import ChallengeReviewedBadge, {
  isChallengeReviewCurrent,
} from "../components/ChallengeReviewedBadge";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import ROUTES from "../../router/routesModel";
import { topChromeReservedPx } from "../../layout/components/TopAccountBar";
import SetCompletionView from "../../collection/SetCompletionView";
import { getSetProgress } from "../../collection/collectionApi";
import { useCurrency } from "../../providers/currencyContext";
import { summariseEstimatedValue } from "../../utils/priceAdapter";
import { formatTotals } from "../../utils/currency";
import { NO_PRICE_LABEL } from "../cardDisplay";
import { useScrollContainer } from "../../layout/ScrollContainerContext";
import { dvh } from "../../styles/viewportHeight";
import CollectionTileChunk from "./CollectionTileChunk";

// The public trade list's sharing dialog (docs/PUBLIC_TRADE_LIST.md). Split
// out: this page is in the main chunk, and only the button below is needed
// until someone taps it.
const TradeListShareDialog = lazy(() => import("../../tradeList/TradeListShareDialog"));

// B-55: tiles drawn per batch as the grid scrolls (a few screens on a phone),
// and the size of each run of tiles that steps aside when far from view.
const TILE_BATCH = 24;
const TILE_GRID = { display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 14 };

// ── Tab config ───────────────────────────────────────────────────────────────
// Note: the "vault" tab maps to the backend collectionType "glory" (the enum
// value is unchanged to avoid a data migration — only the user-facing label is
// "Vault"). "Vault" = the cards you keep, not for trade or sale.
const TABS = [
  { key: "glory", label: "Vault", desc: "Your keepers — not for trade or sale" },
  { key: "trade", label: "Trade", desc: "Available to trade" },
  { key: "sale",  label: "Sale",  desc: "Listed for sale" },
  { key: "sets",  label: "Sets",  desc: "Track your set completion progress" },
];

// Group UserCards by set (uses populated cardId for set metadata)
const groupBySet = (userCards) => {
  const map = {};
  for (const uc of userCards) {
    const card = uc.cardId;
    if (!card) continue;
    const setId   = card.set?.id   || "unknown";
    const setName = card.set?.name || "Legacy";
    if (!map[setId]) map[setId] = { setId, setName, cards: [] };
    map[setId].cards.push(uc);
  }
  return Object.values(map).sort((a, b) => {
    if (a.setId === "unknown") return 1;
    if (b.setId === "unknown") return -1;
    return a.setName.localeCompare(b.setName);
  });
};

// A UserCard as the price adapter wants to see it: the populated catalog
// document plus the grade of THIS copy. One shape, used by the tab total, the
// per-set subtotal and the tile — so the three cannot disagree.
const priceRow = (uc) => ({ card: uc?.cardId, condition: uc?.condition });

/**
 * The header line under a total. A total that silently omits the cards we have
 * no price for would read as complete when it isn't, so the omission is stated
 * rather than absorbed into the number.
 */
const unpricedNote = ({ unpriced }) =>
  unpriced > 0 ? ` · ${unpriced} unpriced` : "";

/**
 * A total, as text — or, when there is no total to state, what the tiles say.
 *
 * TWO rules live here, one from each side of this merge, and the gate is the
 * one quantity that satisfies both: `currencies`, the list of codes that
 * actually contributed a positive figure.
 *
 * #23 — a list where nothing can be priced must not print a total.
 * `summariseEstimatedValue` is honest about that, but `formatMoney` only
 * suppresses null/undefined/"", so a finite 0 came out as a confident "€0.00"
 * above a grid where every tile said "No estimate". Zero is a price; "we don't
 * know" is not zero.
 *
 * #64 — a tab holding a Cardmarket euro AND a provider-quoted dollar has NO
 * single total, so the adapter returns `total: null` for it on purpose. A gate
 * reading `total > 0` is therefore false on exactly the mixed collection the
 * "€412.30 + $4.20" line below exists to render, and blanked it.
 *
 * `currencies` cannot diverge from what is about to be printed: it is built by
 * `partitionByCurrency` from the same positive, currency-tagged amounts the
 * line renders, so an empty list means there is literally nothing to show —
 * including the case a `priced > 0` gate got wrong (a 1-cent common held at
 * Damaged, ×0.3, rounds to 0.00 and contributes nothing).
 *
 * When there is no total, the fallback says BOTH things: the tiles' own words,
 * and how many cards are behind them.
 */
const estimateText = (estimate, formatAmount, label = "") => {
  if (estimate.currencies.length > 0) {
    // One currency prints the single figure this header has always printed; two
    // list both, joined by "+", never added — there is no approved rate that
    // could turn "€412.30 and $4.20" into one number, and dropping the minority
    // currency into the unpriced bucket would hide money we know the exact
    // value of. `formatAmount` takes the code as its second argument, which is
    // what keeps a dollar subtotal from rendering under a euro sign.
    const figure = formatTotals(estimate.byCurrency, estimate.currencies, formatAmount);
    return `${label}${figure}${unpricedNote(estimate)}`;
  }
  // Nothing to state. No `label`: "Est. No estimate" is not a sentence.
  return `${NO_PRICE_LABEL}${unpricedNote(estimate)}`;
};

/**
 * The tab My Collection opens on (owner decision 2026-09-27): the first of
 * Vault → Trade → Sale that holds cards, so a collector whose cards are all
 * listed does not land on "No Vault cards yet". Nothing anywhere keeps the
 * Vault, whose empty state is the "add your first card" prompt.
 */
export const collectionTabFor = (byType) =>
  ["glory", "trade", "sale"].find((k) => (byType?.[k] || []).length > 0) || "glory";

/**
 * The one-line Sets teaser: started sets that are not finished, and the
 * closest of them. Null when there is nothing worth pointing at.
 */
export const setsTeaserFor = (sets) => {
  const list = Array.isArray(sets) ? sets : [];
  const started = list.filter((s) => s.owned > 0 && s.total > 0);
  if (started.length === 0) return null;
  const inProgress = started.filter((s) => s.owned < s.total);
  const complete = started.length - inProgress.length;
  if (inProgress.length === 0) {
    return { text: `${complete} ${complete === 1 ? "set" : "sets"} complete`, percent: 100 };
  }
  const closest = Math.max(...inProgress.map((s) => Number(s.percentage) || 0));
  return {
    text: `${inProgress.length} ${inProgress.length === 1 ? "set" : "sets"} in progress · closest: ${closest}%`,
    percent: closest,
  };
};

// ── Page ─────────────────────────────────────────────────────────────────────
const CollectionPage = () => {
  const {
    collectionByType, collectionCount, isLoading, hasLoadedOnce, isSaving, error,
    toggleCollection, isInCollection, refreshCollection,
  } = useCollection();
  const { user } = useUser();
  const navigate  = useNavigate();
  const { formatAmount } = useCurrency();

  const [activeType, setActiveType] = useState("glory");
  // The opening tab is chosen once, after the collection's first real load. A
  // `?list=` deep link or the user's own tap decides it instead, and nothing
  // switches tabs by itself afterwards.
  const tabDecided = useRef(false);
  const [setsTeaser, setSetsTeaser] = useState(null);
  const [activeSet,  setActiveSet]  = useState(null);
  // Both halves: the catalog card the modal renders, and the UserCard record it
  // must not overwrite. Without the record the modal fell back to add-form
  // defaults and re-graded the card Near Mint with empty notes (P1-36).
  const [listModalCard, setListModalCard] = useState(null);
  const [listModalUserCard, setListModalUserCard] = useState(null);
  // Destination the listing dialog starts on — set only by the "List it for
  // trade" deep link below; null keeps the dialog's normal prefill.
  const [listPreset, setListPreset] = useState(null);
  // [B-39] A listing the sheet saved before Android closed the app (usually
  // behind the photo picker or the camera), handed back to it once.
  const [listResume, setListResume] = useState(null);
  // The public trade list's sharing dialog (docs/PUBLIC_TRADE_LIST.md).
  const [shareOpen, setShareOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const listParam = searchParams.get("list");
  const [detailCard, setDetailCard] = useState(null);
  const [detailOwnerPhotos, setDetailOwnerPhotos] = useState([]);
  const [detailCondition, setDetailCondition] = useState(undefined);
  // B-14: the copy the sheet stands for. Its "in a trade" answer is read from
  // the rows on every render, not copied when the sheet opens, so a reload that
  // lands while the sheet is up (a trade accepted or ended) is reflected.
  const [detailCopyId, setDetailCopyId] = useState(null);
  const [challengeUserCard, setChallengeUserCard] = useState(null);
  const [challengeRecords, setChallengeRecords] = useState([]);
  const [challengeLoading, setChallengeLoading] = useState(false);
  const [challengeError, setChallengeError] = useState("");

  // Cards and set grouping for the active tab
  const activeUserCards = useMemo(
    () => collectionByType[activeType] || [],
    [collectionByType, activeType],
  );
  const activeBySet = useMemo(
    () => groupBySet(activeUserCards),
    [activeUserCards],
  );

  // Tab-local set filter.
  //
  // The selection is checked against the sets actually present in the tab
  // before it is applied. Moving the filtered set's last card to another tab
  // leaves `activeSet` pointing at a set that is no longer here; filtering on
  // it emptied `visibleSets` while `activeUserCards` was still non-empty, so
  // the `activeUserCards.length === 0` branch below was skipped AND the grid
  // mapped over nothing — the cards still in the tab rendered nowhere (#69).
  const effectiveSet = activeBySet.some((s) => s.setId === activeSet)
    ? activeSet
    : null;
  const visibleSets = effectiveSet
    ? activeBySet.filter((s) => s.setId === effectiveSet)
    : activeBySet;

  // B-55: the grid draws its tiles in batches. Every card of the tab used to be
  // drawn at once: with 2,003 cards the page had 35,665 elements, a 745 MB
  // WebView renderer and 46% janky frames. Now the first batch is drawn, and
  // the next one when a sentinel below the last tile comes within 800 px of the
  // visible area. Each batch-sized run of a set's tiles then steps aside again
  // once it is far from view (CollectionTileChunk), so scrolling to the end
  // does not put every tile back on the page. Set chips, counts and totals
  // still read every card. A new tab or set filter starts again from the first
  // batch. Without IntersectionObserver everything is drawn, as before.
  const canObserve = typeof IntersectionObserver !== "undefined";
  const viewKey = `${activeType}|${effectiveSet || ""}`;
  const [drawn, setDrawn] = useState({ key: viewKey, count: TILE_BATCH });
  // Forget the count as soon as the view changes, before anything is drawn.
  // Only drawMore wrote the key, so a view that never drew more (Sets, a tab
  // or set of 24 or fewer) kept the old one, and coming back restored it:
  // every tile of a tab scrolled to the end mounted in one render.
  if (drawn.key !== viewKey) setDrawn({ key: viewKey, count: TILE_BATCH });
  const tileLimit = !canObserve ? Infinity : drawn.key === viewKey ? drawn.count : TILE_BATCH;
  const visibleTotal = visibleSets.reduce((n, s) => n + s.cards.length, 0);
  const drawMore = () =>
    setDrawn({ key: viewKey, count: tileLimit + TILE_BATCH });
  const scrollRoot = useScrollContainer();
  const [sentinel, setSentinel] = useState(null);
  useEffect(() => {
    if (!sentinel || !canObserve) return undefined;
    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((e) => e.isIntersecting)) return;
      observer.disconnect();
      drawMore();
    }, { root: scrollRoot, rootMargin: "0px 0px 800px 0px" });
    // Re-created after every batch: a sentinel still in range after the grid
    // grew would otherwise never report again.
    observer.observe(sentinel);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sentinel, scrollRoot, viewKey, tileLimit]);

  // Estimated value of the WHOLE collection. [B-41] The header line reads
  // "3 cards total · Est. …", and the value used to be the open tab's alone,
  // so Vault said €350 and Trade €230 for cards worth €580 together. The count
  // is every card, so the value is every card too.
  //
  // Was a hand-rolled `sum + (uc?.cardId?.cardmarket?.prices?.avg30 || 0)`,
  // which read a raw field the price adapter does not prefer and skipped the
  // condition multiplier entirely — so this header contradicted the tiles
  // directly beneath it (P1-47). It goes through the adapter now, like
  // everything else that puts a price on screen.
  const allUserCards = useMemo(
    () => [].concat(
      collectionByType.glory || [], collectionByType.trade || [], collectionByType.sale || [],
    ),
    [collectionByType],
  );
  // T-62 (tablet QA 2026-10-23): a copy promised to an accepted trade is still
  // in the collection until the swap completes, but it is not the collector's
  // to keep, and one promised Squirtle was ~64% of an "Est. $147.31". So the
  // line states the two parts, never their sum: "Est." is what stays, and
  // "in trades" is what is promised (B-14's `promisedTo`, owner rows only).
  // Together they still cover every card the count counts (B-41).
  const isPromised = (uc) => uc?.promisedTo?.length > 0;
  const totalValue = useMemo(
    () => summariseEstimatedValue(allUserCards.filter((uc) => !isPromised(uc)).map(priceRow)),
    [allUserCards],
  );
  const promisedCards = useMemo(() => allUserCards.filter(isPromised), [allUserCards]);
  const promisedValue = useMemo(
    () => summariseEstimatedValue(promisedCards.map(priceRow)),
    [promisedCards],
  );

  // "List it for trade" (first-card moment, P0 area B): `/collection?list=<catalog
  // card id>` opens that card's listing dialog pointed at the Trade Binder, where
  // the photo step is required. Waits for the collection to load, then consumes
  // the param so a reload or Back does not reopen it.
  useEffect(() => {
    if (!listParam || isLoading) return;
    const all = [
      ...(collectionByType.glory || []),
      ...(collectionByType.trade || []),
      ...(collectionByType.sale || []),
    ];
    const uc = all.find((row) => String(row?.cardId?._id || row?.cardId) === String(listParam));
    const next = new URLSearchParams(searchParams);
    next.delete("list");
    setSearchParams(next, { replace: true });
    if (!uc || !uc.cardId || typeof uc.cardId !== "object") return;
    setActiveType(uc.collectionType || "glory");
    setListPreset("trade");
    setListModalUserCard(uc);
    setListModalCard(uc.cardId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listParam, isLoading, collectionByType]);

  // [B-39] Reopen a listing Android closed the app on, once the collection has
  // loaded, on the same copy. The picked photo is not recoverable: the sheet's
  // picker is the WebView's own file input, and after the app is closed Android
  // hands its result to a Capacitor callback that no longer has anything to
  // give it to (BridgeWebChromeClient keeps it in memory only). The App
  // plugin's `appRestoredResult` covers Capacitor plugin calls, not this input.
  // So the rest comes back, and the sheet asks for the photo again.
  const listResumeChecked = useRef(false);
  useEffect(() => {
    if (listResumeChecked.current || !hasLoadedOnce || isLoading || listParam) return;
    listResumeChecked.current = true;
    const draft = readDraft("listing", user?._id);
    const uc = draft && allUserCards.find((row) => row?._id === draft.id);
    if (!uc || !uc.cardId || typeof uc.cardId !== "object") return;
    setListResume(draft);
    setListModalUserCard(uc);
    setListModalCard(uc.cardId);
  }, [hasLoadedOnce, isLoading, listParam, allUserCards, user?._id]);

  // Opening tab: first of Vault → Trade → Sale with cards, once. A `?list=`
  // deep link has already chosen (above), so it only marks the choice made.
  useEffect(() => {
    if (tabDecided.current) return;
    if (listParam) {
      tabDecided.current = true;
      return;
    }
    if (!hasLoadedOnce || isLoading) return;
    tabDecided.current = true;
    setActiveType(collectionTabFor(collectionByType));
  }, [listParam, hasLoadedOnce, isLoading, collectionByType]);

  // Set Completion lives in the fourth tab, where most collectors never look.
  // One compact line on the main screen points at it — only when a set has
  // actually been started, and never instead of the cards.
  useEffect(() => {
    if (!hasLoadedOnce || collectionCount === 0) {
      setSetsTeaser(null);
      return undefined;
    }
    let alive = true;
    getSetProgress()
      .then((sets) => { if (alive) setSetsTeaser(setsTeaserFor(sets)); })
      .catch(() => { if (alive) setSetsTeaser(null); });
    return () => { alive = false; };
  }, [hasLoadedOnce, collectionCount]);

  useEffect(() => {
    let alive = true;
    const loadChallenges = async () => {
      if (!user?._id || isLoading || collectionCount === 0) {
        setChallengeRecords([]);
        return;
      }
      setChallengeLoading(true);
      setChallengeError("");
      try {
        const challenges = await getMyCardChallenges();
        if (alive) setChallengeRecords(challenges);
      } catch (err) {
        if (alive) {
          setChallengeError(err?.message || "Challenge status unavailable");
          setChallengeRecords([]);
        }
      } finally {
        if (alive) setChallengeLoading(false);
      }
    };
    loadChallenges();
    return () => {
      alive = false;
    };
  }, [user?._id, isLoading, collectionCount]);

  const challengeByUserCardId = useMemo(() => {
    const map = {};
    for (const c of challengeRecords) {
      const key = c?.userCardId?.toString?.() || c?.userCardId;
      if (key && !map[key]) map[key] = c;
    }
    return map;
  }, [challengeRecords]);

  const upsertChallengeRecord = (updatedChallenge) => {
    if (!updatedChallenge?._id || !updatedChallenge?.userCardId) return;
    setChallengeRecords((prev) => {
      const without = prev.filter((c) => c._id !== updatedChallenge._id);
      return [updatedChallenge, ...without];
    });
  };

  // ── Not logged in ─────────────────────────────────────────────────────────
  if (!user) {
    return (
      <div style={centeredEmpty}>
        <Inventory2OutlinedIcon sx={{ fontSize: 52, color: "#cbd5e1", mb: 2 }} />
        <p style={emptyTitle}>Your collection awaits</p>
        <p style={emptyDesc}>Log in to start building your collection</p>
        <button onClick={() => navigate(ROUTES.LOGIN)} style={ctaBtn}>Log In</button>
      </div>
    );
  }

  // ── Loading ───────────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div style={centeredEmpty}>
        <Loader context="grid" label="Loading your collection" />
        <p style={{ marginTop: 16, color: "#64748b", fontWeight: 600 }}>Loading your collection…</p>
      </div>
    );
  }

  // ── Error ─────────────────────────────────────────────────────────────────
  if (error) {
    return (
      <div style={centeredEmpty}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>⚠️</div>
        <p style={emptyTitle}>Couldn't load your collection</p>
        <p style={emptyDesc}>{error}</p>
        <button onClick={refreshCollection} style={ctaBtn}>Try again</button>
      </div>
    );
  }

  // ── Main ──────────────────────────────────────────────────────────────────
  let tilesLeft = tileLimit;
  return (
    <div style={{ minHeight: dvh(100), background: "#f8f7fa", fontFamily: "Inter, sans-serif" }}>

      {/* ── HEADER ── */}
      <div style={{
        position: "sticky", top: 0, zIndex: 20,
        background: "rgba(248,247,250,0.94)", backdropFilter: "blur(16px)",
        WebkitBackdropFilter: "blur(16px)", padding: "20px 16px 0",
        borderBottom: "1px solid rgba(107,33,168,0.08)",
      }}>
        {/* The floating TopAccountBar owns the top-right corner, so the title row
            reserves that band and the estimate sits on the subtitle line rather
            than under the avatar. */}
        <div style={{
          display: "flex", alignItems: "center", gap: 12, marginBottom: 16,
          paddingRight: topChromeReservedPx(ROUTES.COLLECTION),
        }}>
          <button onClick={() => navigate(-1)} style={backBtn} aria-label="Back">
            <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="#0f172a" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 900, color: "#0f172a", letterSpacing: "-0.5px" }}>
              My Collection
            </h1>
            <p style={{ margin: 0, fontSize: 13, color: "#64748b", fontWeight: 500 }}>
              {collectionCount} {collectionCount === 1 ? "card" : "cards"} total
              {allUserCards.length > promisedCards.length && (
                <>
                  {" · "}
                  <span data-testid="collection-est-value" style={{ color: "#6b21a8", fontWeight: 800 }}>
                    {estimateText(totalValue, formatAmount, "Est. ")}
                  </span>
                </>
              )}
              {promisedCards.length > 0 && (
                <>
                  {" · "}
                  <span data-testid="collection-promised-value" style={{ fontWeight: 700 }}>
                    {promisedValue.currencies.length > 0
                      ? `${formatTotals(promisedValue.byCurrency, promisedValue.currencies, formatAmount)} in trades`
                      : `${promisedCards.length} in trades`}
                  </span>
                </>
              )}
              {isSaving && " · saving…"}
            </p>
          </div>
        </div>

        {/* ── Type tabs: Glory | Trade | Sale ── */}
        <div style={{ display: "flex", gap: 0, marginBottom: 0 }}>
          {TABS.map((tab) => {
            const count = tab.key === "sets" ? null : (collectionByType[tab.key] || []).length;
            const active = activeType === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => { tabDecided.current = true; setActiveType(tab.key); setActiveSet(null); }}
                style={{
                  flex: 1, padding: "10px 4px 12px",
                  border: "none",
                  borderBottom: active ? "3px solid #6b21a8" : "3px solid transparent",
                  background: "transparent",
                  cursor: "pointer",
                  fontFamily: "Inter, sans-serif",
                  transition: "all 0.15s",
                }}
              >
                <div style={{ fontSize: 11, fontWeight: 800, color: active ? "#6b21a8" : "#64748b", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                  {tab.label}
                </div>
                <div style={{ fontSize: 16, fontWeight: 900, color: active ? "#0f172a" : "#64748b", marginTop: 2 }}>
                  {count !== null ? count : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} style={{ verticalAlign: "middle" }}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {/* ── Sets teaser: opens the Sets tab ── */}
        {activeType !== "sets" && setsTeaser && (
          <button
            type="button"
            data-testid="collection-sets-teaser"
            onClick={() => { tabDecided.current = true; setActiveType("sets"); setActiveSet(null); }}
            style={{
              display: "flex", alignItems: "center", gap: 10, width: "100%",
              margin: "10px 0 2px", padding: "10px 12px",
              border: "1px solid #e9d5ff", borderRadius: 12,
              background: "linear-gradient(135deg, #faf5ff 0%, #f5f3ff 100%)",
              cursor: "pointer", fontFamily: "Inter, sans-serif", textAlign: "left",
            }}
          >
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: "block", fontSize: 13, fontWeight: 800, color: "#4c1d95" }}>
                {setsTeaser.text}
              </span>
              <span style={{ display: "block", height: 4, marginTop: 6, borderRadius: 2, background: "#ede9fe" }}>
                <span style={{ display: "block", height: 4, width: `${setsTeaser.percent}%`, borderRadius: 2, background: "#7c3aed" }} />
              </span>
            </span>
            <span style={{ fontSize: 12, fontWeight: 800, color: "#6b21a8", whiteSpace: "nowrap" }}>View sets ›</span>
          </button>
        )}

        {/* ── Set filter chips (for active tab, not "sets" tab) ── */}
        {activeType !== "sets" && activeBySet.length > 1 && (
          <div className="hide-scrollbar" style={{ display: "flex", gap: 8, overflowX: "auto", padding: "10px 0 12px", whiteSpace: "nowrap" }}>
            {/* Lit off the applied filter, not the raw state, so the row never
                shows a selection that matches no chip on it. */}
            <button onClick={() => setActiveSet(null)} style={chipStyle(effectiveSet === null)}>
              All Sets
            </button>
            {activeBySet.map((s) => (
              <button
                key={s.setId}
                onClick={() => setActiveSet(s.setId === activeSet ? null : s.setId)}
                style={chipStyle(activeSet === s.setId)}
              >
                {s.setName} ({s.cards.length})
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ── CONTENT ── */}
      <div style={{ padding: "16px 16px 120px" }}>

        {activeType === "sets" ? (
          <SetCompletionView />
        ) : (
        <>
        {/* Tab description */}
        {activeUserCards.length > 0 && (
          <p style={{ margin: "0 0 16px", fontSize: 12, color: "#64748b", fontWeight: 500 }}>
            {TABS.find((t) => t.key === activeType)?.desc}
            {challengeLoading && " · loading challenge status..."}
            {challengeError && ` · ${challengeError}`}
          </p>
        )}
        {activeType === "trade" && activeUserCards.length > 0 && (
          <button
            type="button"
            onClick={() => setShareOpen(true)}
            data-testid="collection-share-trade-list"
            style={shareListBtn}
          >
            Share my trade list
          </button>
        )}

        {activeUserCards.length === 0 ? (
          <div style={{ textAlign: "center", padding: "60px 0" }}>
            <Inventory2OutlinedIcon sx={{ fontSize: 44, color: "#cbd5e1", mb: 1.5 }} />
            <p style={emptyTitle}>
              No {TABS.find((t) => t.key === activeType)?.label || activeType} cards yet
            </p>
            {/* Honest about what each tab does (P0 area B): the Vault shows on
                your profile but never matches; only Trade Binder / For Sale
                cards WITH a photo can be matched. The old Trade-tab copy pointed
                at a control that does not exist ("change its status"). */}
            <p style={emptyDesc} data-testid={`collection-empty-desc-${activeType}`}>
              {activeType === "glory"
                ? "Add cards from the Market — they go to your Vault. Vault cards show on your profile but can't be traded or matched."
                : activeType === "trade"
                ? "Nothing up for trade yet. Tap List on any Vault card, choose Trade Binder and add a photo — that's what lets nearby collectors match with you."
                : "Tap List on any Vault or Trade card and choose For Sale."}
            </p>
            {activeType === "glory" && (
              <button onClick={() => navigate(ROUTES.POKEMON_CARDS)} style={ctaBtn}>
                Browse Market
              </button>
            )}
            {activeType !== "glory" && (collectionByType.glory || []).length > 0 && (
              <button
                data-testid="collection-empty-go-vault"
                onClick={() => setActiveType("glory")}
                style={ctaBtn}
              >
                Choose a card from your Vault
              </button>
            )}
            {activeType !== "glory" && (collectionByType.glory || []).length === 0 && (
              <button onClick={() => navigate(ROUTES.POKEMON_CARDS)} style={ctaBtn}>
                Browse Market
              </button>
            )}
          </div>
        ) : (
          <>
          {visibleSets.map((setGroup) => {
            // A set past the batch is not drawn; the set it ends in is drawn
            // in part, under a header that still counts all of its cards.
            if (tilesLeft <= 0) return null;
            const shownCards = setGroup.cards.slice(0, tilesLeft);
            tilesLeft -= shownCards.length;
            return (
            // Keyed by tab too: a run's measured box must not carry over to
            // the same set on another tab, where the run holds other cards.
            <div key={activeType + setGroup.setId} style={{ marginBottom: 32 }}>
              {/* Set header */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                <div>
                  <h2 style={{ margin: 0, fontSize: 16, fontWeight: 900, color: "#0f172a", letterSpacing: "-0.3px" }}>
                    {setGroup.setName === "unknown" ? "Legacy Cards" : setGroup.setName}
                  </h2>
                  <p
                    data-testid={`collection-set-subtotal-${setGroup.setId}`}
                    style={{ margin: "2px 0 0", fontSize: 12, color: "#64748b", fontWeight: 500 }}
                  >
                    {setGroup.cards.length} {setGroup.cards.length === 1 ? "card" : "cards"}
                    {" · "}
                    {(() => {
                      const subtotal = summariseEstimatedValue(setGroup.cards.map(priceRow));
                      return estimateText(subtotal, formatAmount);
                    })()}
                  </p>
                </div>
                <div style={{
                  background: "rgba(107,33,168,0.08)", borderRadius: 999,
                  padding: "4px 12px", fontSize: 12, fontWeight: 800, color: "#6b21a8",
                }}>
                  {setGroup.cards.length}
                </div>
              </div>

              {/* Cards grid: one 2-column run per batch of tiles, 14 px apart
                  like the rows inside a run, so the layout is unchanged. A
                  run's key carries its size, so a run that grows or shrinks
                  is measured again instead of keeping an old box. */}
              <div style={{ display: "grid", gap: 14 }}>
                {Array.from({ length: Math.ceil(shownCards.length / TILE_BATCH) }, (_, run) => {
                  const runCards = shownCards.slice(run * TILE_BATCH, (run + 1) * TILE_BATCH);
                  return (
                <CollectionTileChunk key={`${run}:${runCards.length}`} root={scrollRoot} style={TILE_GRID}>
                {() => runCards.map((uc) => {
                  const pokemonCard = getCardData(uc);
                  const challenge = challengeByUserCardId[uc._id?.toString?.() || uc._id];
                  const ownerPhotos = uc.photos || [];
                  const ownerPhotoUrl = ownerPhotos[0]?.url;
                  return (
                    <div key={uc._id} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      <Card
                        pokemonCard={pokemonCard}
                        // The grade recorded for THIS copy, so the tile prices
                        // the card the owner actually holds — and so it agrees
                        // with the totals above, which apply the same
                        // multiplier (P1-47).
                        condition={uc.condition}
                        ownerPhotoUrl={ownerPhotoUrl}
                        onClick={() => {
                          setDetailCard(pokemonCard);
                          setDetailOwnerPhotos(ownerPhotos);
                          // The sheet opens on the same copy the tile stands
                          // for, so it has to be handed the same grade — it
                          // used to quote Near Mint over a tile that had
                          // already priced a Damaged card.
                          setDetailCondition(uc.condition);
                          setDetailCopyId(uc._id);
                        }}
                        onAddToCollection={() => toggleCollection(pokemonCard)}
                        onListForSale={() => {
                          setListModalCard(pokemonCard);
                          setListModalUserCard(uc);
                        }}
                        inCollection={isInCollection(pokemonCard._id)}
                        // B-14: from the server's `promisedTo` on this copy.
                        promised={uc.promisedTo?.length > 0}
                      />
                      <span style={challengeStatusStyle(challenge)}>
                        {getChallengeStatusLabel(challenge)}
                      </span>
                      {/*
                        The owner's screen used to be the ONLY place a challenge
                        result appeared, and it showed the admin's decision rather
                        than what anyone else could see. Those are now two
                        different facts: the line above is what the admin decided,
                        this badge is what a counterparty is actually shown. They
                        diverge whenever a badge has expired, been revoked, or been
                        cleared because the photos changed — and an owner who
                        cannot see that divergence cannot know to act on it.
                      */}
                      <ChallengeReviewedBadge
                        reviewedAt={uc.challengeReviewedAt}
                        variant="compact"
                      />
                      {needsRechallenge(uc, challenge) && (
                        <span style={rechallengeHint}>
                          No longer shown to others — run it again
                        </span>
                      )}
                      <button
                        onClick={() => setChallengeUserCard(uc)}
                        style={challengeBtn}
                        aria-label={`${
                          needsRechallenge(uc, challenge) ? "Run a new" : "Start"
                        } photo challenge for ${pokemonCard?.name || "card"}`}
                      >
                        {needsRechallenge(uc, challenge) ? "Run Challenge Again" : "Photo Challenge"}
                      </button>
                    </div>
                  );
                })}
                </CollectionTileChunk>
                  );
                })}
              </div>
            </div>
            );
          })}
          {visibleTotal > tileLimit && (
            <button
              ref={setSentinel}
              type="button"
              data-testid="collection-draw-more"
              onClick={drawMore}
              style={{ ...challengeBtn, padding: "12px 8px" }}
            >
              Show more cards ({visibleTotal - tileLimit} left)
            </button>
          )}
          </>
        )}
        </>
        )}
      </div>

      {/* ── Card detail ── */}
      <CardDetailModal
        open={!!detailCard}
        card={detailCard}
        ownerPhotos={detailOwnerPhotos}
        condition={detailCondition}
        promised={activeUserCards.some((uc) => uc._id === detailCopyId && uc.promisedTo?.length > 0)}
        onClose={() => {
          setDetailCard(null);
          setDetailOwnerPhotos([]);
          setDetailCondition(undefined);
          setDetailCopyId(null);
        }}
        onAddToCollection={(card) => toggleCollection(card)}
        inCollection={!!detailCard && isInCollection(detailCard._id)}
      />

      {/* ── ListCardModal ── */}
      <ListCardModal
        open={!!listModalCard}
        onClose={() => {
          setListModalCard(null);
          setListModalUserCard(null);
          setListPreset(null);
          setListResume(null);
        }}
        card={listModalCard}
        existingUserCard={listModalUserCard}
        initialCollectionType={listPreset}
        draftUserId={user?._id}
        resume={listResume}
        onSuccess={(result) => {
          setListModalCard(null);
          setListModalUserCard(null);
          setListPreset(null);
          setListResume(null);
          // Follow the card to the tab it now lives on. Listing a Vault card for
          // trade used to leave the page on the emptied Vault ("No Vault cards
          // yet"), so a first-time collector watched their card vanish (device
          // run, 2026-09-27).
          if (result?.collectionType) setActiveType(result.collectionType);
          refreshCollection();
        }}
      />
      <CardChallengeModal
        open={!!challengeUserCard}
        onClose={() => setChallengeUserCard(null)}
        userCard={challengeUserCard}
        currentChallenge={
          challengeUserCard
            ? challengeByUserCardId[challengeUserCard._id?.toString?.() || challengeUserCard._id]
            : null
        }
        onChallengeUpdated={upsertChallengeRecord}
      />
      {shareOpen && (
        <Suspense fallback={null}>
          <TradeListShareDialog open={shareOpen} onClose={() => setShareOpen(false)} />
        </Suspense>
      )}
    </div>
  );
};

/* ── Shared micro-styles ── */
const centeredEmpty = {
  minHeight: dvh(100), background: "#f8f7fa", fontFamily: "Inter, sans-serif",
  display: "flex", flexDirection: "column", alignItems: "center",
  justifyContent: "center", padding: 24,
};
const emptyTitle = { fontSize: 18, fontWeight: 800, color: "#0f172a", margin: "0 0 8px" };
const emptyDesc  = { fontSize: 14, color: "#64748b", margin: "0 0 24px", textAlign: "center", maxWidth: 300 };
const ctaBtn = {
  background: "#6b21a8", color: "#fff", border: "none", borderRadius: 14,
  padding: "13px 28px", fontSize: 15, fontWeight: 800, cursor: "pointer",
  fontFamily: "Inter, sans-serif", boxShadow: "0 4px 16px rgba(107,33,168,0.35)",
};
const backBtn = {
  background: "#fff", border: "1.5px solid rgba(0,0,0,0.08)", borderRadius: 12,
  padding: "8px 10px", cursor: "pointer", display: "flex", alignItems: "center",
  // 44: the touch rule — the icon + padding alone measured ~38dp (T-25).
  minWidth: 44, minHeight: 44, justifyContent: "center",
};
const chipStyle = (active) => ({
  flexShrink: 0, padding: "8px 16px", borderRadius: 12, minHeight: 44, // T-25: was ~35dp
  border: active ? "none" : "1.5px solid rgba(0,0,0,0.08)",
  background: active ? "#1a0a2e" : "#fff",
  color: active ? "#fff" : "#475569",
  fontSize: 12, fontWeight: 800, fontFamily: "Inter, sans-serif",
  cursor: "pointer", outline: "none", transition: "all 0.15s",
  boxShadow: active ? "0 4px 14px rgba(26,10,46,0.25)" : "0 1px 4px rgba(0,0,0,0.06)",
});
const getChallengeStatusLabel = (challenge) => {
  if (!challenge) return "Challenge not started";
  if (isExpiredChallenge(challenge)) return "Challenge expired";
  if (challenge.status === "pending") return "Photo challenge pending";
  if (challenge.status === "submitted") return "Submitted for review";
  if (challenge.status === "approved") return "Challenge evidence reviewed";
  if (challenge.status === "rejected") return "Challenge evidence was not accepted for review compliance.";
  return "Challenge not started";
};
/**
 * An approved review that is no longer published, so the owner can start a new one.
 *
 * A badge lasts 90 days and an admin can revoke it, so "approved" and "currently
 * shown to other people" drift apart over time. An expiring badge with no route
 * back would be worse than no badge at all — the owner would watch their evidence
 * quietly stop counting with nothing to do about it.
 */
const needsRechallenge = (userCard, challenge) =>
  challenge?.status === "approved" &&
  !isChallengeReviewCurrent(userCard?.challengeReviewedAt);

const rechallengeHint = {
  display: "block",
  color: "#a16207",
  fontSize: 9.5,
  fontWeight: 700,
  lineHeight: 1.25,
  textAlign: "center",
};

const challengeStatusStyle = (challenge) => {
  const expired = isExpiredChallenge(challenge);
  const color = !challenge ? "#64748b"
    : expired ? "#f97316"
    : challenge.status === "approved" ? "#047857"
    : challenge.status === "rejected" ? "#b91c1c"
    : challenge.status === "submitted" ? "#2563eb"
    : "#6b21a8";
  return {
    display: "block",
    color,
    fontSize: 10,
    fontWeight: 800,
    lineHeight: 1.25,
    textAlign: "center",
    minHeight: 13,
  };
};
const shareListBtn = {
  display: "block",
  margin: "0 0 16px",
  border: "1.5px solid #6b21a8",
  background: "#fff",
  color: "#6b21a8",
  borderRadius: 999,
  padding: "8px 16px",
  fontSize: 13,
  fontWeight: 800,
  fontFamily: "Inter, sans-serif",
  cursor: "pointer",
};
const challengeBtn = {
  width: "100%",
  border: "1.5px solid rgba(107,33,168,0.16)",
  background: "#fff",
  color: "#6b21a8",
  borderRadius: 12,
  padding: "9px 8px",
  fontSize: 12,
  fontWeight: 900,
  fontFamily: "Inter, sans-serif",
  cursor: "pointer",
  boxShadow: "0 1px 6px rgba(0,0,0,0.05)",
};

export default CollectionPage;
